import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Stream } from '../types/stream';
import { StreamService } from '../services/stream_service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../environments/environment';

import { StreamSubscriptionPlan } from '../types/stream-subscription-plan';
import { TeamService } from '../services/team_service';
import { Util } from '../util/util';
import { StreamSubscription } from '../types/stream_subscription';
import { GtmService } from '../services/gtm_service';
import { Router } from '@angular/router';
import { DOCUMENT, Location } from '@angular/common';
import { Team } from '../types/team';

@Component({
    selector: 'subscribe-plans',
    templateUrl: './subscribe-plans.component.html',
    styleUrls: ['./subscribe-plans.component.scss']
})
export class SubscriptionPlansComponent implements OnInit {
    @ViewChild('subscribePlansModal', {static: false}) subscribePlansModal: ElementRef;

    @Input()
    public streamId: number;

    @Input()
    public team: Team;

    @Input()
    public isLoggedIn: boolean;

    @Input()
    public isStreamFree: boolean;

    @Input()
    public checkSubStatus = false;

    @Output()
    public subscriptionsStatus: EventEmitter<any> = new EventEmitter();

    public streamPlans: StreamSubscriptionPlan[];
    public teamPlans: StreamSubscriptionPlan[];
    public subscriptionStatus: {
        expired: boolean;
        expiresDate: number;
        plans: {
            stream: {
                id: number | string;
                expires: number;
            };
            team: {
                id: number | string;
                expires: number;
            };
        };
    } = {
        expired: false,
        expiresDate: 0,
        plans: {
            stream: {
                id: 0,
                expires: 0,
            },
            team: {
                id: 0,
                expires: 0,
            }
        }
    };

    public teamStreams: Stream[];

    public selectedPlan: {
        type: string;
        token: string;
        teamId: string;
        plan: any;
    };

    public loadingRedirection = {
        status: false,
        type: '',
        planType: ''
    };

    public showGoBackButton = false;
    public shareEmail: {
        stream: boolean,
        team: boolean,
    } = {
        stream: true,
        team: true,
    };

    private modalRef: any;

    constructor(
        private utilService: Util,
        private streamService: StreamService,
        @Inject(DOCUMENT) private document,
        private teamService: TeamService,
        private gtmService: GtmService,
        private location: Location,
        private modalService: NgbModal) { }

    ngOnInit() {
        if ((this.streamId || this.team) && this.isLoggedIn) {
            this.getSubscriptions();
        }
    }

    public showSubscriptionsModal(): void {
        console.log('Show Subscribe Modal', this);
        this.modalRef = this.modalService.open(this.subscribePlansModal, { windowClass: 'subscription-plans-modal', size: 'lg' });

        this.modalRef.result.then(result => {
            console.log(`Closed with:`, result);
        }, reason => {
            console.log(`Dismissed`, reason);
            if (this.selectedPlan) {
                delete this.selectedPlan;
            }
        })
        .catch(e => {
            console.log('error while closing modal: ', e);
        });
    }

    public subscribeToPlan(plan: any, type: string): void {
        this.showGoBackButton = false;
        const token = plan.token;
        const teamId = type === 'team' ? this.team.id : '';
        const transactRedirectUrl = environment.transactBaseUrl + '/purchase';
        const returnUrl = environment.urlBasePrefix + this.location.path();

        this.loadingRedirection.status = true;
        this.loadingRedirection.planType = type;
        this.selectedPlan = {
            type,
            token,
            teamId: teamId || '',
            plan
        };

        if (this.isStreamFree) {
            this.validateFreeSubscription();
        } else if (!this.isStreamFree) {
            this[type === 'stream' ? 'streamService' : 'teamService'].subscriptionRedirectPayment(
                type === 'stream' ? this.selectedPlan.plan.channelId : teamId,
                this.selectedPlan.plan.periodStr === 'YEAR' ? 'YEARLY' : this.selectedPlan.plan.periodStr,
                returnUrl,
                this.shareEmail[type === 'stream' ? 'stream' : 'team']).then(result => {
                if (result.token) {
                    document.location.href = `${transactRedirectUrl}?t=${result.token}`;
                }
            }, err => {
                this.loadingRedirection = {
                    status: true,
                    type: 'error',
                    planType: type
                };
                this.showGoBackButton = true;
                console.error('Error while redirection payment process', err);
            });
        }

    }

    public reloadPage(): void {
        location.reload();
    }

    public onShareEmailChange(type: string, event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        this.shareEmail[type] = isChecked;
    }

    private getSubscriptions(): void {
        if (this.streamId) {
            this.streamService.getStreamTeam(this.streamId).then((team: any) => {
                if (team.length) {
                    this.team = team[0];
    
                    Promise.all([
                        this.streamService.getSubscriptionStatus(this.streamId.toString()),
                        this.teamService.getTeamSubscriptionStatus(this.team.id)
                    ]).then((status: StreamSubscription[]) => {
                        const streamSubscriptionStatus: StreamSubscription = status[0];
                        const teamSubscriptionStatus: StreamSubscription = status[1];
                        const expiredDates: any = [];
    
                        if (teamSubscriptionStatus) {
                            expiredDates.push(teamSubscriptionStatus.expires);
                            this.subscriptionStatus.plans.team.id = teamSubscriptionStatus.planId;
                            this.subscriptionStatus.plans.team.expires = teamSubscriptionStatus.expires;
                        }
    
                        if (streamSubscriptionStatus) {
                            expiredDates.push(streamSubscriptionStatus.expires);
                            this.subscriptionStatus.plans.stream.id = streamSubscriptionStatus.planId;
                            this.subscriptionStatus.plans.stream.expires = streamSubscriptionStatus.expires;
                        }
    
                        const combinedDate = Math.max.apply(null, expiredDates);
    
                        this.subscriptionStatus.expired = new Date().getTime() > combinedDate;
                        this.subscriptionStatus.expiresDate = combinedDate;
    
                        if (!teamSubscriptionStatus && !streamSubscriptionStatus) {
                            this.subscriptionsStatus.emit({required: true});
                        } else {
                            this.subscriptionsStatus.emit(this.subscriptionStatus);
                        }
    
                        this.getTeamPlans();
                        this.getStreamPlans();
                    });
                } else {
                    this.streamService.getSubscriptionStatus(this.streamId.toString()).then(status => {
                        // console.log('stream subscription status', status);
    
                        const streamSubscriptionStatus = status;
                        const expiredDates: any = [];
    
                        if (streamSubscriptionStatus) {
                            expiredDates.push(streamSubscriptionStatus.expires);
                            this.subscriptionStatus.plans.stream.id = streamSubscriptionStatus.planId;
                            this.subscriptionStatus.plans.stream.expires = streamSubscriptionStatus.expires;
    
                            const combinedDate = Math.max.apply(null, expiredDates);
    
                            this.subscriptionStatus.expired = new Date().getTime() > combinedDate;
                            this.subscriptionStatus.expiresDate = combinedDate;
                            this.subscriptionsStatus.emit(this.subscriptionStatus);
                        } else {
                            this.subscriptionsStatus.emit({required: true});
                        }
    
                        this.getStreamPlans();
                    });
                }
            });
        }

        if (this.team) {
            this.teamService.getTeamSubscriptionStatus(this.team.id)
                .then((status: StreamSubscription) => {
                    const teamSubscriptionStatus: StreamSubscription = status;
                    const expiredDates: any = [];

                    if (teamSubscriptionStatus) {
                        expiredDates.push(teamSubscriptionStatus.expires);
                        this.subscriptionStatus.plans.team.id = teamSubscriptionStatus.planId;
                        this.subscriptionStatus.plans.team.expires = teamSubscriptionStatus.expires;
                    }

                    const combinedDate = Math.max.apply(null, expiredDates);

                    this.subscriptionStatus.expired = new Date().getTime() > combinedDate;
                    this.subscriptionStatus.expiresDate = combinedDate;

                    if (!teamSubscriptionStatus) {
                        this.subscriptionsStatus.emit({required: true});
                    } else {
                        this.subscriptionsStatus.emit(this.subscriptionStatus);
                    }

                    this.getTeamPlans();
                });
        }
        
    }

    private getStreamPlans(): void {
        this.streamService.getStreamSubscriptionPlans(this.streamId.toString()).then((plans: any) => {
            for (const plan of plans) {
                if (
                    this.subscriptionStatus &&
                    this.subscriptionStatus.plans.stream.id &&
                    this.subscriptionStatus.plans.stream.expires) {
                    if (plan.id === this.subscriptionStatus.plans.stream.id) {
                        plan.expired = new Date().getTime() > this.subscriptionStatus.plans.stream.expires;
                        plan.expiresDate = this.subscriptionStatus.plans.stream.expires;
                    }
                }

                this.streamService.getStreamSubscriptionToken(this.streamId.toString(), plan.periodStr)
                    .then((token: {token: string}) => {
                        plan.token = token;
                    });
            }

            this.streamPlans = plans.sort((a, b) => (a.period > b.period) ? 1 : -1);
        }, err => {
            console.error('Error fetching stream subscription plans', err);
        });
    }

    private getTeamPlans(): void {
        this.teamService.getTeamSubscriptionPlans(this.team.id).then((plans: any) => {
            for (const plan of plans) {
                if (
                    this.subscriptionStatus &&
                    this.subscriptionStatus.plans.team.id &&
                    this.subscriptionStatus.plans.team.expires) {
                    if (plan.id === this.subscriptionStatus.plans.team.id) {
                        plan.expired = new Date().getTime() > this.subscriptionStatus.plans.team.expires;
                        plan.expiresDate = this.subscriptionStatus.plans.team.expires;
                    }
                }

                this.teamService.getTeamSubscriptionToken(this.team.id, plan.periodStr).then((tokenResult: any) => {
                    plan.token = tokenResult.token;
                });
            }

            this.teamPlans = plans.sort((a, b) => (a.period > b.period) ? 1 : -1);
            this.getTeamStreams();
        }, err => {
            console.error('Error fetching stream subscription plans', err);
        });
    }

    private getTeamStreams(): void {
        this.teamService.getTeamChannels(this.team.id).then(teamChannels => {
            this.teamStreams = teamChannels;
        }, err => {
            console.error('Error fetching team streams', err);
        });
    }

    private validateFreeSubscription(): void {
        const selectedPlan = this.selectedPlan;

        this[selectedPlan.type === 'stream' ? 'streamService' : 'teamService']
            .validateSubscription(
                selectedPlan.token,
                selectedPlan.type === 'stream' ? this.streamId.toString() : selectedPlan.teamId)
                    .then(validate => {
                        this.getSubscriptions();
                        this.streamService.subscriptionStatusUpdate(selectedPlan.plan);
                        this.modalRef.close();

                        this.gtmService.pushTag({
                            event: selectedPlan.type === 'stream' ? 'free-subscribe-to-stream' : 'free-subscribe-to-team',
                            subscriptionType: selectedPlan.type,
                            // eslint-disable-next-line max-len
                            [selectedPlan.type === 'stream' ? 'streamId' : 'teamId']: selectedPlan.type === 'stream' ? this.streamId.toString() : selectedPlan.teamId.toString(),
                            streamPlan: selectedPlan.plan
                        });
                    }, err => {
                        console.error(`Error while validation free ${selectedPlan.type} subscription`, err);
                    });
    }
}
